import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell } from 'recharts';

const BUY = 0;
const SELL = 1;
const NEU = 2;

const majorityOverallMsg10 = [  
  'The market is showing early signs of potential growth, but buying signals are still minimal. Investors may cautiously explore new positions while monitoring additional factors for confirmation before committing further.',  
  'Selling signals are emerging, but they are not yet strong enough to suggest significant risk. While it’s important to stay alert to potential downturns, investors can hold their positions for now with some caution.',  
  'With minimal neutral signals, the market shows clearer direction. Investors can take action based on more dominant trends, either buying or selling, as uncertainty is less of a factor at this time.'
];

const majorityOverallMsg50 = [
  'The market is moderately favorable for entering positions, as buying signals indicate potential growth. While opportunities are present, investors should balance optimism with caution, combining this signal with broader market factors.',  
  'Moderate selling signals suggest it’s time to consider reducing risk, as market conditions lean toward a downturn. Investors should evaluate their positions carefully, adjusting based on potential downward trends.',  
  'With neutral signals moderately present, market uncertainty is evident. Investors may prefer to wait for stronger trends before making significant moves, as neither buying nor selling is clearly dominant.'
];

const majorityOverallMsg100 = [  
  'Strong buying signals suggest favorable conditions for growth. This is a good opportunity to enter new positions, but it remains important to consider other factors to ensure a well-rounded investment strategy.',  
  'A significant majority of selling signals indicate a likely downturn. Investors should approach the market with caution, reducing risk or exiting positions as further declines seem probable.',  
  'With neutral signals leading, market uncertainty dominates. Investors should exercise patience, waiting for clearer trends before making major moves, as the current market offers limited guidance on growth or decline.'
];

// majorityMsg

const majorityMsg10 = [  
  'Buying signals are slightly ahead, showing modest potential for growth. While there may be opportunities, they are not yet strong enough to warrant aggressive moves. Investors should proceed cautiously, watching for more definitive trends.',  
  'Selling signals have a slight edge over others, indicating mild market weakness. While some downward trends exist, the pressure to exit positions is low. Investors should monitor for deeper declines before taking significant action.',  
  'Neutral signals are just ahead, reflecting minimal market movement. Investors should be prepared for either growth or decline, as the market could shift in either direction with little warning. Waiting for clearer signals is advisable.'
];

const majorityMsg50 = [  
  'Buying signals are moderately ahead, suggesting decent growth potential. The market appears favorable for cautiously entering positions, but there is still some uncertainty. Investors should balance optimism with careful analysis of broader trends.',  
  'Selling signals have a stronger presence, pointing toward increased market weakness. While the decline is not overwhelming, it’s wise to consider reducing exposure to riskier assets and closely follow trends for further downside.',  
  'With neutral signals moderately ahead, market indecision is clear. Neither growth nor decline is prominent, so careful observation is key. Investors should avoid making large commitments until a stronger direction emerges.'
];

const majorityMsg100 = [  
  'Buying signals are overwhelmingly dominant, signaling a robust market uptrend. This is a highly favorable environment for entering new positions, as confidence in continued growth is strong. Investors can consider more aggressive strategies to capitalize on opportunities.',  
  'Selling signals are dominating, indicating a strong market downturn. This suggests a highly unfavorable environment for holding positions. Investors should consider reducing their holdings to mitigate losses and prepare for further declines.',
  'Neutral signals are strongly in control, showing a market lacking clear direction. It’s a time for patience, as neither buying nor selling is decisively indicated. Investors should stay on the sidelines until a clearer trend develops.'
];

// minorityMsg

const minorityMsg10 = [  
  'With buying signals significantly trailing, market conditions for growth are currently very unfavorable. Investors should avoid aggressive buying as opportunities are extremely limited, focusing instead on more stable strategies.',  
  'With very few selling signals, the market shows little risk of a significant downturn. This stability indicates favorable conditions for holding or entering positions, though investors should stay vigilant for potential shifts.',  
  'With very few neutral signals, the market has a clear direction. Investors can confidently align their actions with either strong buying or selling trends, minimizing uncertainty and acting decisively based on the dominant signals.'
];

const minorityMsg50 = [
  
  'Buying signals are trailing, indicating limited growth potential. While the environment is not overwhelmingly negative, investors may prefer to adopt cautious strategies, waiting for stronger upward momentum to emerge before taking significant action.',  
  'Selling signals are relatively low, suggesting less risk of a significant downturn. The market appears stable enough to hold positions, though it’s wise to keep an eye on emerging trends that may shift the balance toward selling.',  
  'With neutral signals trailing behind, the market is showing more direction. Investors can make informed decisions based on the prevailing buying or selling trends, though some uncertainty may still exist in the market.'
];

const minorityMsg100 = [
  
  'Buying signals are extremely limited, indicating very poor conditions for growth. Investors should avoid new positions and focus on minimizing risk. There is little to no upward momentum in the market at this time.',  
  'Selling signals are virtually absent, indicating a highly stable or even optimistic market. This suggests favorable conditions for holding or entering positions, as the risk of a significant decline is minimal.',  
  'With almost no neutral signals, the market is showing a very clear direction. Investors can act decisively, following the strong trends in buying or selling without hesitation, as uncertainty is largely absent.'
];

// middleMsg

const middleMsg10 = [  
  'With buying signals at a low but not trailing, growth is possible but uncertain. Investors may explore new opportunities cautiously, remaining watchful of other market signals before committing to more significant positions.',  
  'Selling signals are present but minimal, indicating limited downside risks. Investors can hold their positions, with some caution, as market declines are not a major concern but could still emerge.',  
  'Neutral signals are low, but some uncertainty persists. The market is showing a clearer direction, though investors may still adopt a balanced strategy, keeping an eye on shifts in either buying or selling trends.'
];

const middleMsg50 = [
  
  'With buying signals at moderate levels, the market shows some optimism, though it’s not dominant. Investors can explore opportunities while staying flexible, as the balance could shift toward other signals.',  
  'Moderate selling signals suggest some caution, but there’s no overwhelming pressure to exit positions. Investors can make slight adjustments to manage risk, while keeping an eye on broader market movements.',  
  'With neutral signals moderately present, the market remains somewhat unclear. Investors can make selective decisions, combining action with careful observation, awaiting stronger trends for clearer investment strategies.'
];

const middleMsg100 = [  
  'Buying signals are somewhat present, but not strong enough to lead the market. Growth opportunities exist but are mixed, so investors should proceed with care, balancing optimism with caution in their decisions.',  
  'Selling signals are notable but not dominant, reflecting some market downturns without overwhelming pressure. Investors may choose to reduce exposure carefully, preparing for potential shifts in market sentiment.',  
  'Neutral signals dominate but don’t overwhelm, showing a market with some uncertainty. Investors should maintain a balanced approach, ready to adapt to stronger buying or selling trends as they emerge.'
];

const COLORS = ['#0ef80b', '#ff2bc5', '#908f8f'];

const PieChartBuySellStrategies = ({data, symbolIcon}) => {
  // const data = datad.data;
  // const [data, setData] = useState([]);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [total, setTotal] = useState(0);
  const [isOverall, setIsOverall] = useState(true);
  const [message, setMessage] = useState('');
  const [emoji, setEmoji] = useState('emoji-expressionless');

  const onMouseEnter = (_, index) => {
    setHoverIndex(index);
    setIsOverall(false);
    // setEmojiIcon(data[index].value);
  };

  const onMouseLeave = () => {
    setHoverIndex(null);
    setIsOverall(true);
    // setEmojiIcon(0);
  };

  useEffect(() => {
    if (data.length > 0) {
      setTotal(data[BUY].value + data[SELL].value + data[NEU].value);
    }
  }, [data]);
  
  useEffect(() => {    

    const getMessage = () => {     

      if (!data) return;

      const buyPerc = data[BUY].value / total * 100;
      const sellPerc = data[SELL].value / total * 100;
      const neuPerc = data[NEU].value / total * 100;
    
      let msg = '';
    
      // Logic set 1
      if (buyPerc > sellPerc && buyPerc > neuPerc) {
        if(hoverIndex === BUY){
          if(buyPerc < 10){
            msg = majorityMsg10[BUY];            
            setEmoji('emoji-neutral');
          } 
          if(buyPerc > 10 && buyPerc <= 50){ 
            msg = majorityMsg50[BUY];        
            setEmoji('emoji-smile');    
          }
          if(buyPerc > 50){ 
            msg = majorityMsg100[BUY];
            setEmoji('emoji-laughing');    
          }
        }
        else if(hoverIndex === SELL){
          if(sellPerc < 10){ 
            msg = sellPerc > neuPerc ? middleMsg10[SELL] : minorityMsg10[SELL];            
            setEmoji(sellPerc > neuPerc ? 'emoji-neutral' : 'emoji-expressionless');
          }
          if(sellPerc > 10 && sellPerc <= 50){ 
            msg = sellPerc > neuPerc ? middleMsg50[SELL] : minorityMsg50[SELL];
            setEmoji(sellPerc > neuPerc ? 'emoji-astonished' : 'emoji-neutral');
          }
          if(sellPerc > 50){ 
            msg = sellPerc > neuPerc ? middleMsg100[SELL] : minorityMsg100[SELL];
            setEmoji(sellPerc > neuPerc ? 'emoji-surprise' : 'emoji-grimace');
          }
        }
        else if(hoverIndex === NEU){
          if(neuPerc < 10){ 
            msg = neuPerc > sellPerc ? middleMsg10[NEU] : minorityMsg10[NEU];            
            setEmoji(neuPerc > sellPerc ? 'emoji-expressionless' : 'emoji-neutral');
          }
          if(neuPerc > 10 && neuPerc <= 50){ 
            msg = neuPerc > sellPerc ? middleMsg50[NEU] : minorityMsg50[NEU];
            setEmoji(neuPerc > sellPerc ? 'emoji-neutral' : 'emoji-astonished');
          }
          if(neuPerc > 50){
             msg = neuPerc > sellPerc ? middleMsg100[NEU] : minorityMsg100[NEU];
             setEmoji(neuPerc > sellPerc ? 'emoji-grimace' : 'emoji-surprise');
          }
        }
        else{
          if(buyPerc < 10){            
            msg = majorityOverallMsg10[BUY]
            setEmoji('emoji-neutral');
          } 
          if(buyPerc > 10 && buyPerc <= 50){ 
            msg = majorityOverallMsg50[BUY]
            setEmoji('emoji-smile');
          }
          if(buyPerc > 50){ 
            msg = majorityOverallMsg100[BUY]
            setEmoji('emoji-laughing');
          }
        }
      } 
      // Logic set 2
      else if (sellPerc > buyPerc && sellPerc > neuPerc) {
        if(hoverIndex === SELL){
          if(sellPerc < 10){
            msg = majorityMsg10[SELL];            
            setEmoji('emoji-neutral');
          } 
          if(sellPerc > 10 && sellPerc <= 50){ 
            msg = majorityMsg50[SELL];
            setEmoji('emoji-grimace');
          }
          if(sellPerc > 50){ 
            msg = majorityMsg100[SELL];
            setEmoji('emoji-surprise');
          }
        }
        else if(hoverIndex === BUY){
          if(buyPerc < 10){ 
            msg = buyPerc > neuPerc ? middleMsg10[BUY] : minorityMsg10[BUY];            
            setEmoji(buyPerc > neuPerc ? 'emoji-neutral' : 'emoji-frown');
          }
          if(buyPerc > 10 && buyPerc <= 50){ 
            msg = buyPerc > neuPerc ? middleMsg50[BUY] : minorityMsg50[BUY];
            setEmoji(buyPerc > neuPerc ? 'emoji-expressionless' : 'emoji-neutral');
          }
          if(buyPerc > 50){ 
            msg = buyPerc > neuPerc ? middleMsg100[BUY] : minorityMsg100[BUY];
            setEmoji(buyPerc > neuPerc ? 'emoji-wink' : 'emoji-smile');
          }
        }
        else if(hoverIndex === NEU){
          if(neuPerc < 10){ 
            msg = neuPerc > buyPerc ? middleMsg10[NEU] : minorityMsg10[NEU];
            setEmoji(neuPerc > sellPerc ? 'emoji-surprise' : 'emoji-dizzy');
          }
          if(neuPerc > 10 && buyPerc <= 50){ 
            msg = neuPerc > buyPerc ? middleMsg50[NEU] : minorityMsg50[NEU];
            setEmoji(neuPerc > sellPerc ? 'emoji-neutral' : 'emoji-expressionless');
          }
          if(neuPerc > 50){
             msg = neuPerc > buyPerc ? middleMsg100[NEU] : minorityMsg100[NEU];
             setEmoji(neuPerc > sellPerc ? 'emoji-neutral' : 'emoji-expressionless');
          }
        }
        else{
          if(sellPerc < 10){            
            msg = majorityOverallMsg10[SELL];
            setEmoji('emoji-neutral');
          } 
          if(sellPerc > 10 && sellPerc <= 50){ 
            msg = majorityOverallMsg50[SELL];
            setEmoji('emoji-grimace');
          }
          if(sellPerc > 50){ 
            msg = majorityOverallMsg100[SELL];
            setEmoji('emoji-surprise');
          }
        }
      } 
      // Logic set 3
      else if (neuPerc > buyPerc && neuPerc > sellPerc) {
        if(hoverIndex === NEU){
          if(neuPerc < 10){
            msg = majorityMsg10[NEU];            
            setEmoji('emoji-surprise');
          } 
          if(neuPerc > 10 && neuPerc <= 50){ 
            msg = majorityMsg50[NEU];            
            setEmoji('emoji-dizzy'); 
          }
          if(neuPerc > 50){ 
            msg = majorityMsg100[NEU];            
            setEmoji('emoji-neutral');
          }
        }
        else if(hoverIndex === SELL){
          if(sellPerc < 10){ 
            msg = sellPerc > buyPerc ? middleMsg10[SELL] : minorityMsg10[SELL];            
            setEmoji(sellPerc > buyPerc ? 'emoji-neutral' : 'emoji-expressionless');
          }
          if(sellPerc > 10 && buyPerc <= 50){ 
            msg = sellPerc > buyPerc ? middleMsg50[SELL] : minorityMsg50[SELL];
            setEmoji(sellPerc > buyPerc ? 'emoji-dizzy' : 'emoji-astonished');
          }
          if(sellPerc > 50){ 
            msg = sellPerc > buyPerc ? middleMsg100[SELL] : minorityMsg100[SELL];
            setEmoji(sellPerc > buyPerc ? 'emoji-surprise' : 'emoji-grimace');
          }
        }
        else if(hoverIndex === BUY){
          if(buyPerc < 10){ 
            msg = buyPerc > sellPerc ? middleMsg10[BUY] : minorityMsg10[BUY];            
            setEmoji(buyPerc > sellPerc ? 'emoji-neutral' : 'emoji-expressionless');
          }
          if(buyPerc > 10 && buyPerc <= 50){ 
            msg = buyPerc > sellPerc ? middleMsg50[BUY] : minorityMsg50[BUY];
            setEmoji(buyPerc > sellPerc ? 'emoji-smile' : 'emoji-neutral');
          }
          if(buyPerc > 50){
             msg = buyPerc > sellPerc ? middleMsg100[BUY] : minorityMsg100[BUY];
             setEmoji(buyPerc > sellPerc ? 'emoji-wink' : 'emoji-smile');
          }
        }
        else{
          if(neuPerc < 10){            
            msg = majorityOverallMsg10[NEU]
            setEmoji('emoji-surprise');
          } 
          if(neuPerc > 10 && neuPerc <= 50){ 
            msg = majorityOverallMsg50[NEU]
            setEmoji('emoji-dizzy');            
          }
          if(neuPerc > 50){ 
            msg = majorityOverallMsg100[NEU]
            setEmoji('emoji-neutral');
          }
        }
      } 
      // Logic set 4
      else { // If the percentages are equal or no clear majority
        //majorityOverallMsg50
        //middleMsg50
        if(hoverIndex === NEU){
          msg = middleMsg50[NEU];
        }
        else if(hoverIndex === SELL){
          msg = middleMsg50[SELL];
        }
        else if(hoverIndex === BUY){
          msg = middleMsg50[BUY];
        }
        else{
          if (buyPerc === sellPerc && sellPerc === neuPerc) {
            msg = "The market presents a balanced view with equal signals for buying, selling, and neutrality. This indicates mixed sentiment, where no clear trend leads. Investors should cautiously assess broader conditions before making any significant trading decisions.";          
          }
          else if (buyPerc === sellPerc && neuPerc === 0){
            msg = "With buying and selling signals equally matched, the market is showing mixed momentum. It’s crucial to weigh both sides carefully, as neither direction is dominant. Investors should evaluate external factors before committing to any positions";
          }
          else if (buyPerc === neuPerc && sellPerc === 0){
            msg = "With buying and neutral signals in balance, the market shows potential for growth, but caution is advised. While there are buying opportunities, the lack of selling signals suggests investors may prefer waiting for clearer trends before taking action.";
          }
          else if (sellPerc === neuPerc && buyPerc === 0){
            msg = "With selling and neutral signals in balance, market caution is advised. The absence of buying signals points to a potential downturn, but the neutral stance suggests uncertainty. Investors may consider reducing risk while waiting for clearer market direction.";
          }
        }
        setEmoji('emoji-expressionless');
      }

      setMessage(msg);

    };
   
    getMessage();

  }, [isOverall, total, hoverIndex, data, symbolIcon]);

  return (
    data.length > 0 && (
      <>       
        <div
          style={{
            display: 'flex',
            border: '1px solid darkgray',
            borderRadius: '10px',
            width: '100%',
            height: '190px',
            marginTop: '20px'
          }}
        >
          {/* Left: Pie Chart */}
          <div style={{ width: '25%', position: 'relative' }}>
            <PieChart width={200} height={200}>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                width={200}
                height={200}
                innerRadius={30}
                outerRadius={70}
                fill="#8884d8"
                paddingAngle={3}
                dataKey="value"
                strokeWidth={0}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                    style={{
                      transition: 'transform 0.9s ease-in-out',
                      transform: hoverIndex === index ? 'scale(1.1)' : 'scale(1)',
                      transformOrigin: 'center'
                    }}
                  />
                ))}
                {/* <LabelList dataKey="value" position="outside" fill="#000" /> */}
              </Pie>
            </PieChart>
  
            <img
              src={symbolIcon} // Replace with your image URL
              alt=""
              style={{
                position: 'absolute',
                top: '52.8%',
                left: '53.0%',
                transform: 'translate(-50%, -50%)',
                width: '50px', // Adjust size as needed
                height: '50px', // Adjust size as needed
                borderRadius: '50%',
                zIndex: 0 // Ensure it appears above the pie chart
              }}
            />
          </div>
  
          {/* Middle: Text Description */}
          <div
            style={{
              width: '35%',
              textAlign: 'left',
              fontSize: '16px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            
            <div style={{ textAlign: 'left', width: '200px', whiteSpace: 'nowrap' }}>
            <h3 style={{ margin:'1px', whiteSpace:'break-spaces', fontSize:'16px' }}>From the {total} strategy variations just generated:</h3>            
              <p style={{ color: hoverIndex === BUY ? COLORS[0] : '#5c5c5c' }}>
                {data[0].value} ({(data[0].value / total * 100).toFixed(2)}%) signal buying.
              </p>
              <p style={{ color: hoverIndex === SELL ? COLORS[1] : '#5c5c5c' }}>
                {data[1].value} ({(data[1].value / total * 100).toFixed(2)}%) imply selling.
              </p>
              <p style={{ color: hoverIndex === NEU ? COLORS[2] : '#5c5c5c' }}>
                {data[2].value} ({(data[2].value / total * 100).toFixed(2)}%) are neutral.
              </p>
            </div>
          </div>
  
          {/* Right: Text Description */}
          <div
            style={{
              width: '40%',
              textAlign: 'left',
              fontSize: '16px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              paddingLeft: '15px'
            }}
          >
            <p>{message}</p>
          </div>
          <i
            style={{
              position: 'relative',
              left: '-0.5%',
              top:'83%',
              fontSize: '20pt'              
            }}
            className={`bi bi-${emoji}`}
          ></i>
        </div>
      </>
    )
  );

};

export default PieChartBuySellStrategies;

